import { Box, Button, Chip, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { useMemo } from "react";
import { useParams } from "react-router";
import { useApiQuery } from "../../hooks";
import { BRAND_ACCENT, SEVERITY_COLORS, theme } from "../../theme/theme";
import type { Issue, UserFlowSeverity } from "../../types";
import { prettyPrintUrl } from "../../utils/url";

const getSeverityColor = (severity: UserFlowSeverity) => {
  switch (severity) {
    case "critical":
      return theme.palette.error.main;
    case "high":
      return theme.palette.warning.main;
    case "medium":
      return SEVERITY_COLORS.medium;
    case "low":
      return theme.palette.success.main;
    default:
      return theme.palette.grey[500];
  }
};

const getSeverityLabel = (severity: UserFlowSeverity) => {
  return severity.charAt(0).toUpperCase() + severity.slice(1);
};

const Loading = ({ text }: { text?: string }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="center"
    spacing={1}
    sx={{
      width: "100%",
      height: "100%",
      flex: 1,
      color: "common.white",
    }}
  >
    <CircularProgress size={16} sx={{ color: BRAND_ACCENT }} />
    <Typography
      variant="subtitle1"
      sx={{
        fontFamily:
          "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        fontWeight: 500,
        fontSize: "20px",
        lineHeight: "1.2em",
        letterSpacing: "-0.04em",
        color: "common.white",
      }}
    >
      {text ?? "desplega.ai is loading..."}
    </Typography>
  </Stack>
);

export const DemoReportPage = () => {
  const { reportId } = useParams();
  const { data: rawIssues, isPending } = useApiQuery<Issue[]>(`/demo/landing-issues/${reportId}`);

  // Group issues by title and count instances, then sort by severity
  const groupedIssues = useMemo(() => {
    if (!rawIssues) return { groups: [], totalCount: 0 };

    // First, filter for open issues only and deduplicate by ID
    const seenIds = new Set<string>();
    const uniqueIssues = rawIssues.filter((issue) => {
      // Only include open issues
      if (issue.issue_status !== "open") {
        return false;
      }
      // Deduplicate by ID
      if (seenIds.has(issue.id)) {
        return false; // Skip this issue as we've already seen this ID
      }
      seenIds.add(issue.id);
      return true;
    });

    const titleGroups = new Map<string, Issue[]>();

    uniqueIssues.forEach((issue) => {
      const title = issue.title;
      if (!titleGroups.has(title)) {
        titleGroups.set(title, []);
      }
      titleGroups.get(title)!.push(issue);
    });

    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };

    const groupedData = Array.from(titleGroups.entries())
      .map(([title, issues]) => ({
        title,
        issues: issues, // Each issue in the group is already unique by ID
        count: issues.length,
        helpUrl: issues[0]?.help_url,
        help: issues[0]?.help,
        severity: issues[0]?.severity || "medium",
      }))
      .sort((a, b) => {
        const severityA = severityOrder[a.severity as keyof typeof severityOrder] ?? 4;
        const severityB = severityOrder[b.severity as keyof typeof severityOrder] ?? 4;
        return severityA - severityB;
      });

    return {
      groups: groupedData,
      totalCount: uniqueIssues.length,
    };
  }, [rawIssues]);

  // Data for pie charts
  const pieChartData = useMemo(() => {
    if (!rawIssues) return { severityData: [], highSeverityData: [] };

    // First, filter for open issues only and deduplicate by ID
    const seenIds = new Set<string>();
    const uniqueIssues = rawIssues.filter((issue) => {
      // Only include open issues
      if (issue.issue_status !== "open") {
        return false;
      }
      // Deduplicate by ID
      if (seenIds.has(issue.id)) {
        return false; // Skip this issue as we've already seen this ID
      }
      seenIds.add(issue.id);
      return true;
    });

    // Calculate severity distribution
    const severityCounts = uniqueIssues.reduce(
      (acc, issue) => {
        const severity = issue.severity || "medium";
        acc[severity] = (acc[severity] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const severityData = Object.entries(severityCounts).map(([severity, count]) => ({
      id: severity,
      value: count,
      label: getSeverityLabel(severity as UserFlowSeverity),
      color: getSeverityColor(severity as UserFlowSeverity),
    }));

    // Calculate high severity issues by title
    const highSeverityIssues = uniqueIssues.filter((issue) => issue.severity === "high");
    const highSeverityCounts = highSeverityIssues.reduce(
      (acc, issue) => {
        const title = issue.title;
        acc[title] = (acc[title] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const highSeverityData = Object.entries(highSeverityCounts).map(([title, count]) => ({
      id: title,
      value: count,
      label: title.length > 30 ? `${title.substring(0, 30)}...` : title,
    }));

    return { severityData, highSeverityData };
  }, [rawIssues]);

  if (isPending) {
    return <Loading text="Loading report..." />;
  }

  if (!rawIssues || rawIssues.length === 0) {
    return (
      <Box
        data-testid="demo-report-page"
        sx={{
          position: "relative",
          overflow: "hidden",
          mx: "auto",
          p: 4,
          minHeight: "100vh",
          bgcolor: "common.black",
          color: "common.white",
          fontFamily:
            "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        }}
      >
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontFamily:
              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "1.2em",
            letterSpacing: "-0.04em",
            color: "common.white",
          }}
        >
          No issues found in this report
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="demo-report-page"
      sx={{
        position: "relative",
        overflow: "hidden",
        mx: "auto",
        p: 4,
        minHeight: "100vh",
        bgcolor: "common.black",
        color: "common.white",
        fontFamily:
          "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {/* Blue circle */}
        <Box
          sx={{
            position: "absolute",
            borderRadius: "50%",
            bgcolor: "primary.main",
            left: "-15%",
            top: "20%",
            width: "40%",
            height: "40%",
            filter: "blur(120px)",
          }}
        />
        {/* Yellow circle */}
        <Box
          sx={{
            position: "absolute",
            borderRadius: "50%",
            background: BRAND_ACCENT,
            right: "-10%",
            top: "10%",
            width: "30%",
            height: "30%",
            filter: "blur(100px)",
          }}
        />
      </Box>

      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6, position: "relative", zIndex: 1 }}>
        <Typography
          variant="h3"
          sx={{
            fontFamily:
              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            fontWeight: 600,
            fontSize: "48px",
            lineHeight: "1.1em",
            letterSpacing: "-0.04em",
            color: BRAND_ACCENT,
            mb: 2,
          }}
        >
          Usability Report for{" "}
          {rawIssues[0]?.url ? `${prettyPrintUrl(rawIssues[0].url, true, true)} ` : ""}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontFamily:
              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "1.2em",
            letterSpacing: "-0.04em",
            color: "rgba(255, 255, 255, 0.8)",
          }}
        ></Typography>
      </Box>

      {/* Main Content with Table of Contents */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* Floating Table of Contents */}
        <Box
          sx={{
            position: "fixed",
            left: "20px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "280px",
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              p: 3,
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(26px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "12px",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                border: `1px solid ${BRAND_ACCENT}`,
              },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily:
                    "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                  fontWeight: 600,
                  fontSize: "18px",
                  lineHeight: "1.2em",
                  letterSpacing: "-0.04em",
                  color: BRAND_ACCENT,
                }}
              >
                Table of Contents
              </Typography>
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: "6px",
                  backgroundColor: "rgba(234, 255, 108, 0.2)",
                  color: BRAND_ACCENT,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  fontFamily:
                    "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                  border: "1px solid rgba(234, 255, 108, 0.3)",
                }}
              >
                {groupedIssues.totalCount}
              </Box>
            </Stack>
            <Stack spacing={1}>
              {groupedIssues.groups.map((group, groupIdx) => (
                <Link
                  key={groupIdx}
                  href={`#issue-${groupIdx}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    borderRadius: "8px",
                    textDecoration: "none",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "rgba(234, 255, 108, 0.1)",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, mr: 1 }}>
                    <Chip
                      label={getSeverityLabel(group.severity)}
                      size="small"
                      sx={{
                        backgroundColor: getSeverityColor(group.severity),
                        color: "common.white",
                        fontWeight: 600,
                        borderRadius: "2px",
                        fontSize: "0.65rem",
                        fontFamily:
                          "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                        height: "20px",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily:
                          "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "1.3em",
                        letterSpacing: "-0.02em",
                        color: "rgba(255, 255, 255, 0.9)",
                        flex: 1,
                      }}
                    >
                      {group.title}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: "4px",
                      backgroundColor: "rgba(234, 255, 108, 0.2)",
                      color: BRAND_ACCENT,
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                    }}
                  >
                    {group.count}
                  </Box>
                </Link>
              ))}
            </Stack>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ ml: "320px" }}>
          {/* Pie Charts Section */}
          <Box sx={{ mb: 6, position: "relative", zIndex: 1 }}>
            <Box sx={{ display: "flex", gap: 4 }}>
              {/* Severity Distribution Chart */}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    p: 3,
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(26px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    textAlign: "center",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      fontWeight: 600,
                      fontSize: "18px",
                      lineHeight: "1.2em",
                      letterSpacing: "-0.04em",
                      color: BRAND_ACCENT,
                      mb: 2,
                    }}
                  >
                    Issues by Severity
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {pieChartData.severityData.length > 0 ? (
                      <PieChart
                        series={[
                          {
                            data: pieChartData.severityData,
                            highlightScope: {
                              fade: "global",
                              highlight: "item",
                            },
                            innerRadius: 30,
                            outerRadius: 100,
                            paddingAngle: 2,
                            cornerRadius: 5,
                            startAngle: -90,
                            endAngle: 270,
                          },
                        ]}
                        width={300}
                        height={250}
                        slots={{
                          legend: () => null,
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontFamily:
                            "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                          color: "rgba(255, 255, 255, 0.6)",
                          fontSize: "14px",
                        }}
                      >
                        No data available
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* High Severity Issues by Title Chart */}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    p: 3,
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(26px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    textAlign: "center",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      fontWeight: 600,
                      fontSize: "18px",
                      lineHeight: "1.2em",
                      letterSpacing: "-0.04em",
                      color: BRAND_ACCENT,
                      mb: 2,
                    }}
                  >
                    High Severity Issues by Type
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {pieChartData.highSeverityData.length > 0 ? (
                      <PieChart
                        series={[
                          {
                            data: pieChartData.highSeverityData,
                            highlightScope: {
                              fade: "global",
                              highlight: "item",
                            },
                            innerRadius: 30,
                            outerRadius: 100,
                            paddingAngle: 2,
                            cornerRadius: 5,
                            startAngle: -90,
                            endAngle: 270,
                          },
                        ]}
                        width={300}
                        height={250}
                        slots={{
                          legend: () => null,
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontFamily:
                            "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                          color: "rgba(255, 255, 255, 0.6)",
                          fontSize: "14px",
                        }}
                      >
                        No high severity issues found
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Issues by Type */}
          <Stack spacing={4} sx={{ position: "relative", zIndex: 1 }}>
            {groupedIssues.groups.map((group, groupIdx) => (
              <Box key={groupIdx}>
                {/* Issue Type Header */}
                <Box sx={{ mb: 3 }} id={`issue-${groupIdx}`}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    justifyContent="flex-start"
                  >
                    <Chip
                      label={getSeverityLabel(group.severity)}
                      sx={{
                        backgroundColor: getSeverityColor(group.severity),
                        color: "common.white",
                        borderRadius: "2px",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        fontFamily:
                          "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                        height: "28px",
                      }}
                    />
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily:
                          "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                        fontWeight: 600,
                        fontSize: "32px",
                        lineHeight: "1.2em",
                        letterSpacing: "-0.04em",
                        color: "common.white",
                      }}
                    >
                      {group.title}
                    </Typography>
                    {group.helpUrl && (
                      <Link
                        href={group.helpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily:
                            "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                          fontWeight: 500,
                          fontSize: "16px",
                          lineHeight: "1.2em",
                          letterSpacing: "-0.04em",
                          color: BRAND_ACCENT,
                          textDecoration: "none",
                          "&:hover": {
                            color: BRAND_ACCENT,
                            textDecoration: "underline",
                          },
                        }}
                      >
                        (definition)
                      </Link>
                    )}
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: "8px",
                        backgroundColor: "rgba(234, 255, 108, 0.2)",
                        color: BRAND_ACCENT,
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        fontFamily:
                          "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                        border: "1px solid rgba(234, 255, 108, 0.3)",
                      }}
                    >
                      {group.count} instance{group.count !== 1 ? "s" : ""}
                    </Box>
                  </Stack>
                </Box>

                {/* Individual Issue Instances */}
                <Stack spacing={3}>
                  {group.issues.map((issue, issueIdx) => (
                    <Box
                      key={issueIdx}
                      sx={{
                        p: 4,
                        background: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(26px)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "12px",
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          border: `1px solid ${BRAND_ACCENT}`,
                        },
                      }}
                    >
                      {/* Issue Title and URL */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 2 }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily:
                              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                            fontWeight: 600,
                            fontSize: "20px",
                            lineHeight: "1.2em",
                            letterSpacing: "-0.04em",
                            color: "common.white",
                          }}
                        >
                          {issue.title}
                        </Typography>
                        {issue.url && (
                          <Link
                            href={issue.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              fontFamily:
                                "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                              fontWeight: 500,
                              fontSize: "16px",
                              lineHeight: "1.2em",
                              letterSpacing: "-0.04em",
                              color: BRAND_ACCENT,
                              "&:hover": {
                                color: BRAND_ACCENT,
                              },
                            }}
                          >
                            {prettyPrintUrl(issue.url)}
                          </Link>
                        )}
                      </Stack>

                      {/* Tags */}
                      {issue.tags && issue.tags.length > 0 && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                          {issue.tags.map((tag, tagIdx) => (
                            <Box
                              key={tagIdx}
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "6px",
                                backgroundColor: "rgba(234, 255, 108, 0.2)",
                                color: BRAND_ACCENT,
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                fontFamily:
                                  "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                              }}
                            >
                              {tag}
                            </Box>
                          ))}
                        </Stack>
                      )}

                      {/* Description */}
                      {issue.description && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily:
                                "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                              fontWeight: 400,
                              fontSize: "16px",
                              lineHeight: "1.5em",
                              letterSpacing: "-0.02em",
                              color: "rgba(255, 255, 255, 0.9)",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {issue.description.replace(/\bNone\b/g, "").trim()}
                          </Typography>
                        </Box>
                      )}

                      {/* Recommendations */}
                      {issue.recommendations && issue.recommendations.length > 0 && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontFamily:
                                "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                              fontWeight: 600,
                              fontSize: "16px",
                              lineHeight: "1.2em",
                              letterSpacing: "-0.04em",
                              color: BRAND_ACCENT,
                              mb: 1,
                            }}
                          >
                            Recommendations:
                          </Typography>
                          <Stack spacing={1}>
                            {issue.recommendations.map((rec, recIdx) => (
                              <Typography
                                key={recIdx}
                                variant="body2"
                                sx={{
                                  fontFamily:
                                    "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                                  fontWeight: 400,
                                  fontSize: "15px",
                                  lineHeight: "1.4em",
                                  letterSpacing: "-0.02em",
                                  color: "rgba(255, 255, 255, 0.8)",
                                  pl: 2,
                                  position: "relative",
                                  "&::before": {
                                    content: '"•"',
                                    position: "absolute",
                                    left: 0,
                                    color: BRAND_ACCENT,
                                  },
                                }}
                              >
                                {rec}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* CTA Section */}
      <Box sx={{ textAlign: "center", mt: 8, position: "relative", zIndex: 1 }}>
        <Button
          component={Link}
          href="https://app.desplega.ai"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontFamily:
              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            fontWeight: 600,
            fontSize: "20px",
            lineHeight: "1.2em",
            letterSpacing: "-0.04em",
            background: BRAND_ACCENT,
            color: "common.black",
            borderRadius: "36px",
            px: 6,
            py: 2,
            "&:hover": {
              background: BRAND_ACCENT,
            },
          }}
        >
          Go beyond usability
        </Button>
      </Box>
    </Box>
  );
};
